import React from 'react';
import {COLORS} from '../styles/index';
import {SystemIcons} from '../icons';
import {ErrorMessage, InputWrapper, SearchIconWrapper} from './styling';
import {IconButton} from '../Button';
import SearchBarInput from './components/SearchBarInput';
import SearchField from './components/SearchField';
import {Size} from '../types';
import styled from 'styled-components';
import { DropdownContent, DropdownItem } from '../Dropdown';
import { useClickOutsideRef } from '../common';

export type SearchBarProps = {
  id: string;
  searchTerm?: string;
  setSearchTerm: (term: string) => void;
  enterSearch: (e: any) => void;
  removeSearch: (e: any) => void;
  placeholder?: string;
  performSearchLabel?: string;
  disabled?: boolean;
  validationMessage?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  size?: Size.Small | Size.Medium;
  margin?: string;
  showDropdown?: boolean;
  dropdownButtonIcon?: React.ReactNode;
  dropdownButtonLabel?: string;
  dropdownButtonVariant?: 'primary' | 'secondary' | 'tertiary';
  dropdownButtonAction?: () => boolean | void | undefined;
  searchList?: DropdownItem[];
  addToSearchList?: (e: DropdownItem) => void;
  dropdownScrollable?: boolean;
};

const ClearIconWrapper = styled.div`
  display: flex;
  align-items: center;

  &.hidden {
    display: none !important;
  }
`;

const SearchBar: React.FunctionComponent<SearchBarProps> = ({
                                                             id,
                                                             searchTerm,
                                                             setSearchTerm,
                                                             enterSearch,
                                                             removeSearch,
                                                             placeholder,
                                                             performSearchLabel,
                                                             disabled,
                                                             validationMessage,
                                                             onKeyDown,
                                                             size,
                                                             margin,
                                                             showDropdown,
                                                             dropdownButtonIcon,
                                                             dropdownButtonLabel,
                                                             dropdownButtonVariant,
                                                             dropdownButtonAction,
                                                             searchList=[],
                                                             addToSearchList,
                                                             dropdownScrollable
                                                           }) => {
  const containerRef = React.useRef<any>(null);

  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useClickOutsideRef(()=>setIsOpen(false), [containerRef]);

  const isPressingEnter = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  };

  const enteredSearchTerm = (e: any) => {
    addToSearchList && addToSearchList({value:e.target.value});
    enterSearch(e);
  }

  const dropdownAction = () => {
    if(dropdownButtonAction)
      dropdownButtonAction();    
  }

  return (
    <>
      <InputWrapper
        disabled={disabled}
        margin={margin}>
        <SearchField id={'id'} size={size}
                     ref={containerRef}
                     searchTerm={searchTerm}
                     disabled={disabled}
                     validationMessage={validationMessage}>
          <SearchBarInput
            placeholder={disabled ? '' : placeholder}
            id={id}
            size={size}
            disabled={disabled}
            setSearchTerm={(term: string) => setSearchTerm(term)}
            searchTerm={searchTerm}
            onKeyDown={onKeyDown}
            enterSearch={enteredSearchTerm}
            focusParentRefs={[containerRef]}
            onClick={()=>setIsOpen(!isOpen)}
            ref={dropdownRef}
          />
          <SearchIconWrapper className={size ? size : ''} aria-label={performSearchLabel}>
            <IconButton id={`${id}_Search`}
                        variant="secondary"
                        shape="circular"
                        action={enterSearch}
                        disabled={disabled}
                        onKeyPress={(e: any) => (isPressingEnter(e) ? enterSearch(e) : null)}>
              <SystemIcons.Search size="24px"/>
            </IconButton>
          </SearchIconWrapper>
          <ClearIconWrapper className={(size ? size : '').concat(!searchTerm ? ' hidden' : '')}>
            <IconButton id={`${id}_Clear`}
                        variant="secondary"
                        shape="circular"
                        action={removeSearch}
                        disabled={disabled}
                        onKeyPress={(e: any) => (isPressingEnter(e) ? removeSearch(e) : null)}>
              <SystemIcons.Clear size="24px"/>
            </IconButton>
          </ClearIconWrapper>
        </SearchField>

      {showDropdown && <DropdownContent id={'search'}
                                        containerRef={containerRef}
          size={size}
          isButton={false}
          ariaRolesType="input"
          customizationProps={{
            items:searchList,
            action: dropdownAction,
            actionLabel: dropdownButtonLabel,
            actionIcon: dropdownButtonIcon,
            actionVariant: dropdownButtonVariant,
            onValueUpdate:(e)=>{},
            scrollable: dropdownScrollable,
            pinTopItem: true
          }}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          filter={''}
          selectedValues={searchTerm ? [searchTerm] : []}
          setSelectedValues={(e)=>setSearchTerm(e[0])}
          messageOnNoResults={''}
          focused={null}
          setFocused={()=>{}}
        />}

      </InputWrapper>
      {validationMessage && (
        <ErrorMessage className={size || ''}>
          <SystemIcons.TechnicalWarning color={COLORS.critical_400} className={size || ''}/>
          <span>{validationMessage}</span>
        </ErrorMessage>
      )}
    </>
  );
};

export default SearchBar;
